import React, { useMemo } from 'react';
import {
  BillingAddressInfo,
  StoredPaymentMethodSelect,
  TransactionCategory,
  useBillingInfo,
} from '@shipstation/financial-services-ui-components';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { messages } from './messages';
import fsuiStyles from '../../../../../../financial-services-ui/fsui-styles.module.less';
import style from './plan-summary-payment-method.module.less';
import { emptyObject } from 'ui-components/default-props/empty';
import { PaymentMethod } from '../../../../../../enums/payment-method';
import { getPaymentMethodsFromArray } from '../../../../../../financial-services-ui/mapping';
import { PickerTitle } from '../../../../../../financial-services-ui/picker-title';
import { usePaymentMethodManager } from '../../../../../../hooks/use-payment-method-manager';

/**
 * @typedef {import('@shipstation/financial-services-ui-components').StoredPaymentMethod} StoredPaymentMethod
 * @typedef {import('@shipstation/financial-services-ui-components').BillingInfoData} BillingInfoData
 */

/**
 *
 * @param {object} params
 * @param {() => void} params.onAddAction
 * @param {StoredPaymentMethod | undefined} [params.initialPaymentMethod]
 * @returns {JSX.Element}
 */
export const PlanSummaryPaymentMethod = ({ onAddAction, initialPaymentMethod }) => {
  const intl = useIntl();
  const { billingInfo, setBillingInfo } = useBillingInfo();

  const { handlePaymentMethodChange, selectedPaymentMethod, paymentMethods, isBillingInfoLoading } = usePaymentMethodManager({
    transactionCategory: TransactionCategory.Subscription,
    initialPaymentMethod,
    onBillingInfoUpdated: (info) => setBillingInfo(info, false),
    shouldUseFallbackPaymentMethod: true,
  });

  const isPayByBankMethod = selectedPaymentMethod?.paymentMethod === PaymentMethod.ACH;
  const hasPaymentMethodsToPick = paymentMethods?.length > 0;
  const showBillingInfo = !isPayByBankMethod && hasPaymentMethodsToPick && billingInfo;

  const reconstructedPaymentMethods = useMemo(() => getPaymentMethodsFromArray(paymentMethods), [paymentMethods]);

  const isPaymentMethodsLoaded = paymentMethods !== null;

  if (isBillingInfoLoading || !isPaymentMethodsLoaded) {
    return <LoadingIndicator className={style.loader} />;
  }

  if (!hasPaymentMethodsToPick) {
    return (
      <Button className={style['add-payment-method-button']} onClick={onAddAction} theme={THEME_CONSTANTS.secondary}>
        <FormattedMessage {...messages.addPaymentMethod} />
      </Button>
    );
  }

  return (
    <>
      <PickerTitle
        className={style['payment-method-header']}
        hasPaymentMethodsToPick
        isAdding={false}
        labelTextType={TextType.Label}
        onClick={onAddAction}
      />
      <div className={style['payment-method-section']}>
        <StoredPaymentMethodSelect
          key={paymentMethods?.length}
          className={fsuiStyles.picker}
          disabled={paymentMethods?.length === 0}
          emptyStateMessage={intl.formatMessage(messages.noPaymentMethodsAvailable)}
          hasError={false}
          initialValue={selectedPaymentMethod}
          isFullWidth
          name='paymentMethodSelect'
          onChange={handlePaymentMethodChange}
          paymentMethods={reconstructedPaymentMethods}
          placeholder={intl.formatMessage(messages.selectPaymentMethodPlaceholder)}
          transactionCategory={TransactionCategory.Subscription}
        />
        {showBillingInfo ? <BillingAddressInfo key={selectedPaymentMethod?.id} billingInfo={billingInfo ?? emptyObject} /> : null}
      </div>
    </>
  );
};
